import { createFileRoute, redirect } from "@tanstack/react-router"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { passwordValidationRule, requiredRule } from "@/utilities/form"

export const Route = createFileRoute("/auth/sign-in/set-new-password")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    email?: string
    code?: string
  } => {
    return {
      email: (search.email as string) || "",
      code: (search.code as string) || "",
    }
  },
  beforeLoad: ({ search }) => {
    // Redirect to sign-in if email or code is missing
    if (!search.email || !search.code) {
      // Pass the necessary search object only. Don't want to leak the code accidentally
      throw redirect({
        to: "/auth/sign-in",
        search: {
          qrLookupID: search.qrLookupID,
          redirect: search.redirect,
          plate: search.plate,
          phone: search.phone,
          conversionFlow: search.conversionFlow,
          email: undefined,
        },
      })
    }
  },
  component: RouteComponent,
})

interface SetNewPasswordFormSchema {
  password: string
  confirmPassword: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { email, code, qrLookupID, redirect, phone, plate, conversionFlow } =
    Route.useSearch()
  const { t } = useTranslation(["errors", "common"])

  const form = useForm<SetNewPasswordFormSchema>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onBlur",
  })

  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: SetNewPasswordFormSchema) => {
    if (!email || !code) {
      form.setError("root", {
        message: t("auth.missingEmailOrCode", { ns: "errors" }),
      })
      return
    }

    setIsLoading(true)
    form.clearErrors("root")

    try {
      await api.auth.resetPassword({
        username: email,
        confirmationCode: code,
        newPassword: data.password,
      })

      navigate({
        to: "/auth/sign-in",
        search: {
          qrLookupID,
          redirect,
          phone,
          plate,
          conversionFlow,
          email: undefined,
        },
      })
    } catch (err: any) {
      handleAuthError(err, form, t)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.setNewPassword.heading", { ns: "common" })}
      </Heading>
      <Text level={2} style={{ margin: "0" }}>
        {t("auth.setNewPassword.description", { ns: "common" })}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        {/* Form-wide Error Display */}
        {form.formState.errors.root && (
          <FieldError>{form.formState.errors.root.message}</FieldError>
        )}
        <fieldset style={{ width: "100%", minWidth: 0 }} disabled={isLoading}>
          {/* New Password */}
          <Controller<SetNewPasswordFormSchema>
            name="password"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              ...passwordValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("auth.setNewPassword.newPassword", { ns: "common" })}
                </Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          {/* Confirm Password */}
          <Controller<SetNewPasswordFormSchema>
            name="confirmPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              validate: (value) => {
                const password = form.getValues("password")
                return (
                  value === password ||
                  t("common.passwordMismatch", { ns: "errors" })
                )
              },
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("auth.setNewPassword.confirmPassword", { ns: "common" })}
                </Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          {/* Submit Button */}
          <Button buttonType="primary" type="submit" isPending={isLoading}>
            {isLoading
              ? t("auth.setNewPassword.submittingButton", { ns: "common" })
              : t("auth.setNewPassword.submitButton", { ns: "common" })}
          </Button>
        </fieldset>
      </form>
    </>
  )
}
